import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import apiService from '../../services/api'; 
// 🟢 确保引入 getImgUrl
import { getImgUrl } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ==========================================
// 🟢 [新增组件] 单个单词的 FlashCard 弹窗
// (必须定义在 AssignmentPage 外部，防止 Hook 报错)
// ==========================================
const SingleFlashCardModal = ({ word, onClose, playAudio }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  // 组件挂载时，立即强制预加载图片
  useEffect(() => {
    if (word.imageUrl) {
      const img = new Image();
      img.src = getImgUrl(word.imageUrl);
    }
  }, [word]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} 
        className="relative w-full max-w-lg h-[600px] perspective-1000"
      >
        <button 
            onClick={onClose}
            className="absolute -top-12 right-0 text-white hover:text-red-400 text-xl font-bold transition-colors z-50"
        >
            <AppIcon name="times" className="mr-2" /> Close
        </button>

        <motion.div
            className="relative w-full h-full"
            initial={false}
            animate={{ rotateY: isFlipped ? 180 : 0 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 260, damping: 20 }}
            style={{ transformStyle: 'preserve-3d' }}
        >
            {/* === 正面 === */}
            <div 
                className="absolute inset-0 backface-hidden bg-white rounded-3xl p-8 shadow-2xl flex flex-col border-b-8 border-purple-200 cursor-pointer"
                style={{ backfaceVisibility: 'hidden' }}
                onClick={() => setIsFlipped(true)}
            >
                <div className="text-center mt-8">
                    <h3 className="text-5xl font-extrabold text-slate-800 mb-4">{word.word}</h3>
                    <button 
                        onClick={(e) => { e.stopPropagation(); playAudio(word.word); }}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto transition-transform active:scale-95"
                    >
                        <AppIcon name="volume-up" className="text-xl" />
                    </button>
                    <div className="text-gray-400 italic mt-4 font-serif">/{word.word}/</div>
                </div>

                <div className="bg-slate-50 border-l-4 border-purple-500 p-6 rounded-r-xl my-8">
                    <div className="text-purple-600 font-bold text-xs uppercase mb-2">Definition</div>
                    <p className="text-slate-800 text-lg font-medium">{word.definition}</p>
                </div>

                <div className="mt-auto text-center text-gray-400 text-sm animate-pulse">
                    <AppIcon name="sync-alt" className="mr-2" /> Tap card to flip
                </div>
            </div>

            {/* === 反面 === */}
            <div 
                className="absolute inset-0 backface-hidden bg-white rounded-3xl shadow-2xl cursor-pointer p-6" 
                style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
                onClick={() => setIsFlipped(false)}
            >
                <div className="w-full h-full bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 flex items-center justify-center p-2">
                    {word.imageUrl ? (
                        <img 
                            src={getImgUrl(word.imageUrl)} 
                            className="w-full h-full object-contain rounded-lg" 
                            alt={word.word} 
                            loading="eager"
                        />
                    ) : (
                        <div className="text-gray-400">No Image Available</div>
                    )}
                </div>
            </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// ==========================================
// 子组件：拼写挑战
// ==========================================
const SpellingChallenge = ({ word, onComplete }) => {
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [shake, setShake] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [visualInput, setVisualInput] = useState(''); 

  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setCurrentLetterIndex(0);
    setIsCompleted(false);
    setVisualInput('');
    setInputValue('');
    
    const timer = setTimeout(() => {
        if(inputRef.current) inputRef.current.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [word]);

  const processInput = useCallback((char) => {
    if (isCompleted || !word || !word.word) return;

    const targetChar = word.word[currentLetterIndex].toLowerCase();
    const inputChar = char.toLowerCase();

    if (inputChar.match(/^[a-z]$/)) {
      if (inputChar === targetChar) {
        const nextIndex = currentLetterIndex + 1;
        setCurrentLetterIndex(nextIndex);
        setVisualInput('');
        
        if (nextIndex >= word.word.length) {
          setIsCompleted(true);
          setTimeout(() => onComplete(true), 600); 
        }
      } else {
        setShake(true);
        setVisualInput(inputChar);
        setTimeout(() => {
            setShake(false);
            setVisualInput('');
        }, 300);
      }
    }
  }, [currentLetterIndex, isCompleted, word, onComplete]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.isComposing || e.keyCode === 229) return;

      if (e.key.match(/^[a-zA-Z]$/)) {
        e.preventDefault();
        processInput(e.key);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [processInput]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const char = val.slice(-1);
    setInputValue(''); 
    processInput(char);
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div 
        className="text-center w-full max-w-2xl relative cursor-text select-none outline-none" 
        onClick={handleContainerClick}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        className="absolute top-0 left-0 w-full h-full opacity-0 z-20 cursor-default caret-transparent"
        style={{ fontSize: '16px', pointerEvents: 'none' }} 
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-gramm="false"
        autoFocus
      />

      <div className="inline-block bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider relative z-10">
        <AppIcon name="keyboard" className="mr-1" /> Mission: Spell It
      </div>
      
      <div className="text-xl text-gray-500 mb-8 italic font-medium relative z-10">
        "{word.definition}"
      </div>
      
      <div className="flex flex-wrap justify-center gap-2 mb-8 relative z-10">
        {word.word.split('').map((char, index) => {
          const isDone = index < currentLetterIndex;
          const isActive = index === currentLetterIndex;
          
          let boxStyle = 'bg-gray-100 border-gray-300 text-gray-300';
          if (isDone) {
             boxStyle = 'bg-green-100 border-green-500 text-green-700'; 
          } else if (isActive) {
             if (visualInput) {
                 boxStyle = 'bg-red-100 border-red-500 text-red-700';
             } else {
                 boxStyle = 'bg-white border-purple-500 text-purple-700 shadow-lg scale-110 ring-4 ring-purple-100';
             }
          }

          return (
            <motion.div
              key={index}
              animate={isActive && shake ? { x: [-5, 5, -5, 5, 0] } : {}}
              className={`w-12 h-16 flex items-center justify-center text-3xl font-bold rounded-xl border-b-4 transition-all duration-200 select-none ${boxStyle}`}
            >
              {isDone ? char : (isActive && visualInput ? visualInput : '')}
            </motion.div>
          );
        })}
      </div>
      
      <p className="text-gray-400 text-sm animate-pulse relative z-10">
        Start typing to spell...
      </p>
    </div>
  );
};

// ==========================================
// 子组件：含义选择
// ==========================================
const MeaningChallenge = ({ word, allWords, onComplete }) => {
  const options = useMemo(() => {
    const distractors = allWords
      .filter(w => w.id !== word.id)
      .map(w => w.definition)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
    return [word.definition, ...distractors].sort(() => 0.5 - Math.random());
  }, [word, allWords]);

  return (
    <div className="text-center w-full max-w-2xl">
      <div className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-6 uppercase tracking-wider">
        <AppIcon name="brain" className="mr-1" /> Mission: Identify Meaning
      </div>
      <h2 className="text-5xl font-black text-gray-800 mb-10 tracking-tight">{word.word}</h2>
      <div className="grid gap-4">
        {options.map((option, idx) => (
          <motion.button
            key={idx}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onComplete(option === word.definition)}
            className="w-full p-5 text-left bg-white border-2 border-gray-100 hover:border-blue-500 hover:bg-blue-50 rounded-2xl transition-all font-medium text-gray-700 shadow-sm flex items-center group"
          >
            <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center mr-4 group-hover:bg-blue-500 group-hover:text-white transition-colors font-bold text-sm">
              {['A','B','C','D'][idx]}
            </div>
            <span className="text-lg">{option}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// 主页面：AssignmentPage
// ==========================================
const AssignmentPage = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [stage, setStage] = useState('briefing');
  const [isExpired, setIsExpired] = useState(false);
  const [timeInfo, setTimeInfo] = useState({ dateStr: '', daysLeft: 0 });
  
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [testPlan, setTestPlan] = useState([]);

  // 🟢 控制 FlashCard 弹窗的状态
  const [flashCardWord, setFlashCardWord] = useState(null);

  const calculateTimeRemaining = (dueDateData) => {
    let due;
    if (dueDateData && dueDateData._seconds) {
      due = new Date(dueDateData._seconds * 1000);
    } else {
      due = new Date(dueDateData);
    }

    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    
    return {
      dateStr: due.toLocaleDateString(),
      daysLeft: diffDays,
      isExpired: diffTime < 0
    };
  };

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const data = await apiService.getAssignmentById(id);
        // 🟢 [修复] 保证 vocabulary 始终是数组，否则各处 .length/.map/[i] 在词表缺失时崩溃
        // (作业词汇被删光、或后端返回缺字段时会触发)。
        const safeData = { ...data, vocabulary: Array.isArray(data.vocabulary) ? data.vocabulary : [] };
        setAssignment(safeData);

        const { dateStr, daysLeft, isExpired } = calculateTimeRemaining(safeData.dueDate);
        setTimeInfo({ dateStr, daysLeft });
        setIsExpired(isExpired);

        const plan = safeData.vocabulary.map(w => ({
          wordId: w.id,
          type: (safeData.vocabulary.length > 1 && Math.random() > 0.5) ? 'meaning' : 'spelling'
        }));
        setTestPlan(plan);
      } catch (error) {
        console.error("Assignment load failed", error);
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchAssignment();
  }, [id, navigate]);

  const startMission = () => setStage('review');
  
  const startTest = () => {
    setStartTime(Date.now());
    setStage('test');
  };

  // 🟢 升级版语音播放 (支持英式发音)
  const playAudio = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'en-GB';
      u.rate = 0.9;
      
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => v.lang === 'en-GB' && v.name.includes('Google')) || voices.find(v => v.lang === 'en-GB');
      if (preferredVoice) u.voice = preferredVoice;
      
      window.speechSynthesis.speak(u);
    }
  };

  const handleAnswerAndAdvance = async (isCorrect) => {
    const currentWord = assignment.vocabulary[currentWordIndex];
    const newResult = {
      wordId: currentWord.id,
      word: currentWord.word,
      definition: currentWord.definition,
      isCorrect,
      questionType: testPlan[currentWordIndex].type
    };

    const updatedResults = [...results, newResult];
    setResults(updatedResults);

    if (currentWordIndex < assignment.vocabulary.length - 1) {
      setCurrentWordIndex(prev => prev + 1);
    } else {
      setStage('result');
    }
  };

  const handleSubmitReport = async () => {
    try {
      setLoading(true);
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      const payload = { vocabularyResults: results, totalTimeSpent: timeSpent };
      
      await apiService.submitAssignment(id, payload);
      navigate('/courses'); 
    } catch (error) {
      console.error("Submit failed", error);
      alert("Failed to send report to HQ. Check connection.");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
        <div className="font-mono animate-pulse">ESTABLISHING SECURE LINK...</div>
      </div>
    );
  }

  // Briefing View
  if (stage === 'briefing') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 font-sans">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl relative">
          <div className={`p-8 text-white relative overflow-hidden ${isExpired ? 'bg-gray-800' : 'bg-gradient-to-r from-indigo-900 to-slate-800'}`}>
            <div className="absolute top-0 right-0 p-4 opacity-10"><AppIcon name="user-secret" className="text-9xl" /></div>
            
            {isExpired ? (
                <div className="inline-block bg-red-600 text-white text-xs font-bold px-3 py-1 rounded mb-2">MISSION EXPIRED</div>
            ) : (
                <div className="flex gap-2">
                    <div className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 animate-pulse">TOP SECRET</div>
                    {timeInfo.daysLeft <= 3 && (
                        <div className="inline-block bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded mb-2">
                            {timeInfo.daysLeft === 0 ? 'DUE TODAY' : `${timeInfo.daysLeft} DAYS LEFT`}
                        </div>
                    )}
                </div>
            )}
            
            <h1 className="text-3xl font-extrabold mb-1 tracking-tight">{assignment.title}</h1>
            <p className="opacity-80 text-sm">Mission ID: #{id.slice(0, 6)}</p>
          </div>

          <div className="p-8">
            <div className="flex items-center gap-4 mb-6 text-sm text-gray-600 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <div className="flex items-center"><AppIcon name="user-tie" className="text-indigo-500 mr-2" /> Agent {user?.name || 'Unknown'}</div>
              <div className="w-px h-4 bg-gray-300"></div>
              <div className={`flex items-center ${isExpired ? 'text-red-500 font-bold' : ''}`}>
                  <AppIcon name="calendar-alt" className="text-indigo-500 mr-2" /> 
                  Due: {timeInfo.dateStr}
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div><h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Mission Brief</h3><p className="text-gray-800 font-medium leading-relaxed">{assignment.description || "Complete the vocabulary assessment to secure the objective."}</p></div>
              {assignment.instructions && (<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg"><h4 className="text-yellow-800 font-bold text-sm mb-1"><AppIcon name="lightbulb" className="mr-1" /> Intel from HQ:</h4><p className="text-yellow-900 text-sm">{assignment.instructions}</p></div>)}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-indigo-50 p-4 rounded-2xl text-center border border-indigo-100"><div className="text-3xl font-black text-indigo-600">{assignment.vocabulary.length}</div><div className="text-xs font-bold text-indigo-400 uppercase">Targets</div></div>
              <div className="bg-green-50 p-4 rounded-2xl text-center border border-green-100"><div className="text-3xl font-black text-green-600">+30</div><div className="text-xs font-bold text-green-400 uppercase">XP Reward</div></div>
            </div>

            <button onClick={startMission} disabled={isExpired} className={`w-full font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center group ${isExpired ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-slate-900 hover:bg-black text-white hover:shadow-2xl hover:-translate-y-1 active:scale-95'}`}>{isExpired ? (<span><AppIcon name="lock" className="mr-2" /> MISSION ARCHIVED</span>) : (<><span>ACCEPT MISSION</span><AppIcon name="arrow-right" className="ml-2 group-hover:translate-x-1 transition-transform" /></>)}</button>
            
            <div className="mt-4 text-center">
                <button onClick={() => navigate('/courses')} className="text-sm text-gray-400 hover:text-gray-600 font-medium transition-colors">
                    <AppIcon name="sign-out-alt" className="mr-1" /> Cancel & Return to Dashboard
                </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Review View
  if (stage === 'review') {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center p-6">
        {/* 🟢 插入 FlashCard Modal */}
        <AnimatePresence>
            {flashCardWord && (
                <SingleFlashCardModal 
                    word={flashCardWord} 
                    onClose={() => setFlashCardWord(null)} 
                    playAudio={playAudio} 
                />
            )}
        </AnimatePresence>

        <div className="w-full max-w-5xl">
          <header className="flex justify-between items-center mb-8">
            <div><h2 className="text-2xl font-bold text-slate-800 flex items-center"><span className="bg-blue-100 text-blue-600 p-2 rounded-lg mr-3"><AppIcon name="folder-open" /></span>Intel Review</h2><p className="text-slate-500 text-sm ml-12">Study the targets before engagement.</p></div>
            <button onClick={startTest} className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-red-200 transition-all flex items-center animate-bounce-slow">Start Mission <AppIcon name="crosshairs" className="ml-2" /></button>
          </header>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
            {assignment.vocabulary.map((word, index) => (
              <motion.div 
                key={word.id} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: index * 0.05 }} 
                className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md hover:border-blue-300 transition-all group relative"
              >
                <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-xl text-slate-800">{word.word}</h3>
                    <button onClick={() => playAudio(word.word)} className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:bg-blue-500 hover:text-white transition-colors flex items-center justify-center">
                        <AppIcon name="volume-up" />
                    </button>
                </div>
                <div className="h-px w-full bg-slate-100 mb-3"></div>
                <p className="text-slate-600 text-sm leading-relaxed mb-10">{word.definition}</p>

                {/* 🟢 右下角 FlashCard 图标链接 */}
                <div className="absolute bottom-4 right-4">
                    <button 
                        onClick={() => setFlashCardWord(word)}
                        className="flex items-center gap-2 bg-purple-50 text-purple-600 px-3 py-1.5 rounded-full text-xs font-bold hover:bg-purple-100 hover:text-purple-700 transition-colors shadow-sm border border-purple-100"
                        title="Open FlashCard"
                    >
                        <AppIcon name="expand" /> FlashCard
                    </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Test View
  if (stage === 'test') {
    const currentWord = assignment.vocabulary[currentWordIndex];
    const testType = testPlan[currentWordIndex].type;
    const progress = ((currentWordIndex) / assignment.vocabulary.length) * 100;

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="w-full h-3 bg-slate-200"><motion.div className="h-full bg-gradient-to-r from-blue-500 to-purple-600" animate={{ width: `${progress}%` }} /></div>
        <div className="flex-1 flex flex-col items-center justify-center p-6 relative">
          <div className="absolute top-6 right-6 text-slate-400 font-bold text-sm bg-white px-4 py-2 rounded-full shadow-sm">TARGET {currentWordIndex + 1} / {assignment.vocabulary.length}</div>
          <AnimatePresence mode='wait'>
            <motion.div key={currentWordIndex} initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -50, opacity: 0 }} className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-100 w-full max-w-3xl min-h-[450px] flex flex-col items-center justify-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-purple-500"></div>
              {testType === 'spelling' ? (<SpellingChallenge word={currentWord} onComplete={() => handleAnswerAndAdvance(true)} />) : (<MeaningChallenge word={currentWord} allWords={assignment.vocabulary} onComplete={(isCorrect) => handleAnswerAndAdvance(isCorrect)} />)}
            </motion.div>
          </AnimatePresence>
          
          <div className="mt-8 text-center">
             <button onClick={() => navigate('/courses')} className="text-xs text-gray-400 hover:text-red-500 font-medium transition-colors uppercase tracking-widest">
                <AppIcon name="times" className="mr-1" /> Abort Mission
             </button>
          </div>
        </div>
      </div>
    );
  }

  // Result View
  if (stage === 'result') {
    const correctCount = results.filter(r => r.isCorrect).length;
    const accuracy = Math.round((correctCount / results.length) * 100);
    const isSuccess = accuracy >= 60;

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl">
          <div className={`p-10 text-center text-white relative overflow-hidden ${isSuccess ? 'bg-gradient-to-br from-green-600 to-emerald-700' : 'bg-gradient-to-br from-orange-500 to-red-600'}`}>
            <div className="relative z-10"><div className="text-7xl mb-4 filter drop-shadow-md">{isSuccess ? '🏆' : '⚠️'}</div><h1 className="text-4xl font-extrabold uppercase mb-2 tracking-tight">{isSuccess ? 'Mission Accomplished' : 'Mission Failed'}</h1><p className="opacity-90 font-medium text-lg">{isSuccess ? 'Excellent work, Agent. Report ready for submission.' : 'Accuracy critically low. Reinforcements required.'}</p></div>
          </div>
          <div className="p-8 bg-slate-50">
            <div className="flex justify-center items-center gap-4 mb-8">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 text-center w-1/2"><div className={`text-4xl font-black ${isSuccess ? 'text-green-600' : 'text-red-500'}`}>{accuracy}%</div><div className="text-xs text-gray-400 uppercase font-bold mt-1">Accuracy</div></div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 text-center w-1/2"><div className="text-4xl font-black text-slate-700">{correctCount}/{results.length}</div><div className="text-xs text-gray-400 uppercase font-bold mt-1">Targets Hit</div></div>
            </div>
            {accuracy < 100 && (<div className="mb-8"><h3 className="text-xs font-bold text-red-500 uppercase mb-3 ml-1">Missed Targets</h3><div className="bg-red-50 rounded-xl p-1 border border-red-100 max-h-48 overflow-y-auto custom-scrollbar">{results.filter(r => !r.isCorrect).map((r, idx) => (<div key={idx} className="flex justify-between items-center p-3 border-b border-red-100 last:border-0 hover:bg-red-100/50 transition-colors"><div><span className="font-bold text-slate-700 block">{r.word}</span><span className="text-slate-500 text-xs truncate max-w-[200px] block">{r.definition}</span></div><span className="text-red-500 text-sm"><AppIcon name="times-circle" /></span></div>))}</div></div>)}
            <button onClick={handleSubmitReport} disabled={loading} className={`w-full font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center text-lg ${loading ? 'bg-slate-400 cursor-wait text-white' : 'bg-slate-900 hover:bg-black text-white hover:shadow-xl active:scale-95'}`}>{loading ? (<><AppIcon name="spinner" className="mr-2" /> Submitting Report...</>) : (<><AppIcon name="paper-plane" className="mr-2" /> SUBMIT REPORT & EXIT</>)}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default AssignmentPage;